import React, { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";

const STEPS = [
  {
    title: "Meet The Jade Pendant",
    body: "This is a pre-loaded showcase — a 5-scene romance-thriller powered by ArcScript's emotional intelligence engine. Each scene was generated with a specific core emotion.",
  },
  {
    title: "Emotions Drive the Story",
    body: "See the emotion tags? ArcScript's proprietary taxonomy has 9 core emotions, 33 transitional states, and 8 involuntary responses. The engine doesn't just write — it feels.",
  },
  {
    title: "Quality You Can Measure",
    body: "Every scene is scored against the emotional taxonomy. These aren't generic AI scores — they measure emotional authenticity, narrative tension, and CE alignment.",
  },
  {
    title: "Emotional Arcs, Not Random Scenes",
    body: "Notice how the emotions shift: Romance → Wonder → Fury → Sorrow → Dread. ArcScript ensures each transition is narratively motivated, not arbitrary.",
  },
  {
    title: "Your Turn",
    body: "Scene 6 is ready for you. Click in, pick an emotion, write a brief, and watch the engine generate — weaving continuity from everything that came before.",
  },
];

const STORAGE_KEY = "arcscript_demo_walkthrough_complete";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const DemoWalkthrough = ({ onComplete }) => {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "true") {
      onComplete?.();
    } else {
      setVisible(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    onComplete?.();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      dismiss();
    }
  }, [currentStep, dismiss]);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dismiss]);

  if (!visible) return null;

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;
  const useMotion = !prefersReducedMotion();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/50"
        style={useMotion ? { animation: "fadeIn 0.2s ease" } : {}}
        onClick={dismiss}
        aria-hidden="true"
      />

      {/* Tooltip card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Demo walkthrough step ${currentStep + 1} of ${STEPS.length}`}
        className="fixed z-[61] w-80 rounded-xl p-5"
        style={{
          background: "var(--surface-raised)",
          border: "1px solid var(--accent-gold)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          ...(useMotion ? { animation: "fadeInUp 0.25s ease" } : {}),
        }}
        data-testid="demo-walkthrough"
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-xs font-semibold"
            style={{ color: "var(--ink-tertiary)" }}
          >
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <button
            onClick={dismiss}
            aria-label="Close walkthrough"
            className="transition-colors rounded-md p-0.5"
            style={{ color: "var(--ink-tertiary)" }}
            data-testid="demo-walkthrough-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step content */}
        <h3
          className="font-display text-lg font-semibold mb-2"
          style={{ color: "var(--ink-primary)" }}
        >
          {step.title}
        </h3>
        <p
          className="text-sm leading-relaxed mb-5"
          style={{ color: "var(--ink-secondary)" }}
        >
          {step.body}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-xs transition-colors"
            style={{ color: "var(--ink-tertiary)" }}
            data-testid="demo-walkthrough-skip"
          >
            Skip tour
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: "var(--accent-gold)",
              color: "var(--surface-card)",
            }}
            data-testid="demo-walkthrough-next"
          >
            {isLast ? "Got it" : "Next"}
          </button>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-1.5 mt-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full"
              style={{
                width: i === currentStep ? "16px" : "6px",
                background:
                  i === currentStep
                    ? "var(--accent-gold)"
                    : "var(--border-subtle)",
                transition: useMotion ? "all 0.2s ease" : "none",
              }}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    </>
  );
};
